import { MAX_SEND_BATCH_TOTAL_MB, TOO_MANY_BATCH_FILES_USER_MESSAGE } from "@Front/Constants/uploadLimits";
import {
	ANTIVIRUS_UNAVAILABLE_USER_MESSAGE,
	isAntivirusConnectionUserError,
} from "@Front/Utils/antivirusUserMessages";

export const PAYLOAD_TOO_LARGE_USER_MESSAGE = `L'envoi est trop volumineux : la taille totale des fichiers ne doit pas dépasser ${MAX_SEND_BATCH_TOTAL_MB} Mo en un seul envoi. Envoyez moins de fichiers à la fois ou réduisez leur taille.`;

export { TOO_MANY_BATCH_FILES_USER_MESSAGE };

function looksLikeHtmlErrorBody(text: string): boolean {
	const trimmed = text.trim();
	return (
		trimmed.startsWith("<") &&
		(trimmed.includes("</html>") || trimmed.includes("<body") || trimmed.includes("<h1") || trimmed.includes("<title"))
	);
}

function extractHttpErrorLabelFromHtml(text: string): string | null {
	const h1Match = text.match(/<h1[^>]*>([^<]+)<\/h1>/i);
	if (h1Match?.[1]) {
		return h1Match[1].trim();
	}
	const titleMatch = text.match(/<title[^>]*>([^<]+)<\/title>/i);
	if (titleMatch?.[1]) {
		return titleMatch[1].trim();
	}
	return null;
}

export function isPayloadTooLargeHttpError(httpStatus?: number, rawBody?: string | null): boolean {
	if (httpStatus === 413) {
		return true;
	}
	if (!rawBody || rawBody.trim().length === 0) {
		return false;
	}
	return /413|request entity too large/i.test(rawBody);
}

export function isTooManyBatchFilesHttpError(rawBody?: string | null): boolean {
	if (!rawBody || rawBody.trim().length === 0) {
		return false;
	}
	if (rawBody === TOO_MANY_BATCH_FILES_USER_MESSAGE) {
		return true;
	}
	return /limit_unexpected_file|too many files|unexpected field|plus de \d+ fichiers/i.test(rawBody);
}

/**
 * Maps HTTP status / proxy HTML error pages to a short French message. Returns null when no mapping applies.
 */
export function userFriendlyMessageForHttpError(httpStatus?: number, rawBody?: string | null): string | null {
	if (isPayloadTooLargeHttpError(httpStatus, rawBody)) {
		return PAYLOAD_TOO_LARGE_USER_MESSAGE;
	}
	if (isTooManyBatchFilesHttpError(rawBody)) {
		return TOO_MANY_BATCH_FILES_USER_MESSAGE;
	}
	if (rawBody && isAntivirusConnectionUserError(rawBody)) {
		return ANTIVIRUS_UNAVAILABLE_USER_MESSAGE;
	}
	if (!rawBody || rawBody.trim().length === 0) {
		return null;
	}
	const raw = rawBody.trim();
	if (!looksLikeHtmlErrorBody(raw)) {
		return null;
	}
	const label = extractHttpErrorLabelFromHtml(raw);
	if (label && isPayloadTooLargeHttpError(undefined, label)) {
		return PAYLOAD_TOO_LARGE_USER_MESSAGE;
	}
	return "Le serveur a refusé la requête. Réessayez ou contactez le support.";
}

/**
 * Never expose raw HTML error pages to end users.
 */
export function sanitizeRawApiErrorBodyForUser(message: string): string {
	const trimmed = message.trim();
	if (trimmed.length === 0) {
		return trimmed;
	}
	const mapped = userFriendlyMessageForHttpError(undefined, trimmed);
	if (mapped) {
		return mapped;
	}
	if (looksLikeHtmlErrorBody(trimmed)) {
		return userFriendlyMessageForHttpError(undefined, trimmed) ?? "Le serveur a refusé la requête. Réessayez ou contactez le support.";
	}
	if (isAntivirusConnectionUserError(trimmed)) {
		return ANTIVIRUS_UNAVAILABLE_USER_MESSAGE;
	}
	return trimmed;
}
